/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-09-11 18:20:56
 *
 *******************************************************/
// @flow

import _ from 'lodash';

import React, { Component } from 'react';
import { TextField, Button, TextStyle, Tooltip, Icon } from '@shopify/polaris';
import { QuestionMarkMajor, TickSmallMinor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import TermsConditionsModal from './terms-conditions-modal';

import { formatNumberInThousand } from 'src/helpers';
import plans from 'src/constants/plans.json';
import plansPlus from 'src/constants/plans-plus.json';
import oldPlan from 'src/constants/plans-old.json';
import {
  EXTRA_FEATURES,
  VARIANTS_DISPLAY_EXTRA_FEE_5K_PRODUCTS,
  VARIANTS_DISPLAY_EXTRA_FEE_10K_PRODUCTS
} from 'src/constants/plans-extra-feature';

type Props = {
  host: string,
  showDiscountCodeInput: boolean,
  currentTotalProducts: number,
  price: number,
  totalProducts: number,
  correctPlan: Object,
  selectPlanProducts: number,
  planId: string,
  code: string,
  handleChangeCode: Function,
  verify: Function,
  loading: boolean,
  showVerifyCodeNotice: boolean,
  percent: number,
  subscribe: Function,
  subscribing: boolean,
  allowToSubscribePlanV1: boolean,
  subscribePlusPlanV2: boolean
};

type ComposedProps = Props & WithI18nProps;

class PriceBox extends Component<ComposedProps> {
  state = {
    activeTermsConditionsModal: false
  };

  render() {
    let {
      i18n,
      showDiscountCodeInput,
      currentTotalProducts,
      price,
      totalProducts,
      loading,
      percent,
      termsConditions,
      correctPlan,
      selectPlanProducts,
      subscribing,
      showVerifyCodeNotice,
      code,
      planId,
      subscribePlusPlanV2,
      allowToSubscribePlanV1
    } = this.props;

    const { activeTermsConditionsModal } = this.state;

    const { products: minimalPlanProducts } = correctPlan;
    const plan: Object = allowToSubscribePlanV1
      ? oldPlan[planId]
      : subscribePlusPlanV2
      ? plansPlus[planId]
      : plans[planId];

    // Enable condition:
    // Select correct plan AND
    // (Not discount page OR Discount page and verified discount code input)
    const chooseCorrectPlan =
      selectPlanProducts === undefined || selectPlanProducts >= minimalPlanProducts;

    const allowSubscribe =
      !showDiscountCodeInput || (showDiscountCodeInput && showVerifyCodeNotice && code && percent);

    const warningMessage =
      selectPlanProducts === 0
        ? i18n.translate('AdminHTML.Subscription.PriceBox.invalidUrl')
        : !chooseCorrectPlan
        ? i18n.translate('AdminHTML.Subscription.PriceBox.subscribeHigherPlan', {
            nt_total: formatNumberInThousand(currentTotalProducts)
          })
        : '';

    // Apply extra fee
    let { extraFeeTotal, extraFeaturesFee } = this.calculateExtraFee(selectPlanProducts);
    let totalPrice = price + extraFeeTotal;

    // Apply discount percent to the price
    if (percent) {
      price = (price * (1 - percent)).toFixed(2);
      totalPrice = (totalPrice * (1 - percent)).toFixed(2);
      if (extraFeeTotal) {
        _.map(extraFeaturesFee, feature => {
          feature.fee = (feature.fee * (1 - percent)).toFixed(2);
        });
      }
    }

    const termsConditionsContent = _.get(termsConditions, 'content', '');

    let discountCodePlaceHolder = i18n.translate(
      'AdminHTML.Subscription.PriceBox.discountCodePlaceHolder'
    );

    return (
      <div className="priceBox">
        {/* Plan information - Only Pricing V2 */}
        {!_.isEmpty(plan && !allowToSubscribePlanV1) ? (
          <div className="price-information">
            <div>
              <span className="icon-check">
                <Icon source={TickSmallMinor} />
              </span>
              {i18n.translate('AdminHTML.Subscription.PriceBox.standardFeatures', {
                nt_plan: (
                  <TextStyle variation="strong">
                    <span style={{ marginLeft: '5px' }}>{i18n.translate(plan.label)}</span>
                  </TextStyle>
                )
              })}
            </div>
            <div>
              <span className="icon-check">
                <Icon source={TickSmallMinor} />
              </span>
              <span>
                {i18n.translate('AdminHTML.Subscription.PriceBox.limitProducts', {
                  t1: (
                    <TextStyle variation="strong">
                      <span>
                        {i18n.translate('AdminHTML.Subscription.PriceBox.limitProducts_t1', {
                          nt_number: formatNumberInThousand(plan.products)
                        })}
                      </span>
                    </TextStyle>
                  )
                })}
              </span>
            </div>
            <div>
              <span className="icon-check">
                <Icon source={TickSmallMinor} />
              </span>
              {i18n.translate('AdminHTML.Subscription.PriceBox.standardSupport', {
                nt_type: (
                  <TextStyle variation="strong">
                    <span style={{ marginLeft: '5px' }}>
                      {i18n.translate(
                        `AdminHTML.Subscription.PriceBox.${
                          plan.standardSupport
                            ? 'standardSupport_Type_All'
                            : 'standardSupport_Type_None'
                        }`
                      )}
                    </span>
                  </TextStyle>
                )
              })}
            </div>
            <div>
              <span className="icon-check">
                <Icon source={TickSmallMinor} />
              </span>
              <span style={{ display: 'inline-flex' }}>
                {i18n.translate('AdminHTML.Subscription.PriceBox.premiumServices', {
                  t1_tooltip: (
                    <span className="tooltip long-tooltip">
                      <Tooltip
                        content={
                          <span className="tooltipContent">
                            {i18n.translate(
                              'AdminHTML.Subscription.PriceBox.premiumServices_t1_tooltip'
                            )}
                          </span>
                        }
                        preferredPosition="above">
                        <Icon source={QuestionMarkMajor} color="base" />
                      </Tooltip>
                    </span>
                  )
                })}
              </span>
              <TextStyle variation="strong">
                <span style={{ marginLeft: '5px' }}>
                  {i18n.translate(
                    `AdminHTML.Subscription.PriceBox.${
                      plan.premiumSupport ? 'premiumServices_Type_All' : 'premiumServices_Type_None'
                    }`
                  )}
                </span>
              </TextStyle>
            </div>
          </div>
        ) : (
          ''
        )}
        <div className="price">
          <span className="unit">$</span>
          {totalPrice}
          <span className="unit">{i18n.translate('AdminHTML.Subscription.PriceBox.perMonth')}</span>
        </div>
        {/* Show discount code input */}
        {showDiscountCodeInput ? (
          <div className="discountCode">
            <TextField
              value={code}
              onChange={this.handleChangeCode}
              placeholder={discountCodePlaceHolder}
              connectedRight={
                <Button
                  primary
                  onClick={this.verify}
                  loading={loading}
                  disabled={code ? false : true}>
                  {i18n.translate('AdminHTML.Common.Action.ok')}
                </Button>
              }
            />
          </div>
        ) : (
          ''
        )}
        {/* Code is not empty or percent > 0*/}
        {showDiscountCodeInput && showVerifyCodeNotice && code ? (
          <div className={percent ? 'applied' : 'applied error'}>
            {percent
              ? i18n.translate('AdminHTML.Subscription.PriceBox.discountApplied', {
                  nt_count: percent * 100
                })
              : i18n.translate('AdminHTML.Subscription.PriceBox.invalidDiscount')}
          </div>
        ) : (
          ''
        )}

        {/* Plan details */}
        <div className="description">
          {planId !== 'offer0_1' ? (
            extraFeeTotal ? (
              <span>
                {i18n.translate('AdminHTML.Subscription.PriceBox.descriptionWithExtraFee', {
                  t1: (
                    <strong>
                      {i18n.translate(
                        'AdminHTML.Subscription.PriceBox.descriptionWithExtraFee_t1',
                        {
                          nt_number: price
                        }
                      )}
                    </strong>
                  ),
                  t2: (
                    <strong>
                      {i18n.translate(
                        'AdminHTML.Subscription.PriceBox.descriptionWithExtraFee_t2',
                        {
                          nt_plan: i18n.translate(plan.label)
                        }
                      )}
                    </strong>
                  )
                })}
                {_.map(extraFeaturesFee, (feature, index) => {
                  return i18n.translate(
                    'AdminHTML.Subscription.PriceBox.descriptionWithExtraFeeFeature',
                    {
                      t1: (
                        <strong>
                          {i18n.translate(
                            'AdminHTML.Subscription.PriceBox.descriptionWithExtraFeeFeature_t1',
                            {
                              nt_number: feature.fee
                            }
                          )}
                        </strong>
                      ),
                      t2: (
                        <strong>
                          {i18n.translate(
                            'AdminHTML.Subscription.PriceBox.descriptionWithExtraFeeFeature_t2',
                            {
                              nt_feature: feature.type
                            }
                          )}
                        </strong>
                      ),
                      nt_space: index < extraFeaturesFee.length - 1 ? ', ' : '.'
                    }
                  );
                })}
              </span>
            ) : (
              i18n.translate('AdminHTML.Subscription.PriceBox.description', {
                t1: (
                  <strong>
                    {i18n.translate('AdminHTML.Subscription.PriceBox.description_t1', {
                      nt_number: formatNumberInThousand(totalProducts)
                    })}
                  </strong>
                )
              })
            )
          ) : (
            i18n.translate(
              'AdminHTML.Dashboard.SubscriptionStatus.subscribed_PasswordProtectedOffer_Paragraph'
            )
          )}
        </div>
        {planId !== 'offer0_1' ? (
          <>
            <Button
              primary
              disabled={!chooseCorrectPlan || !allowSubscribe}
              onClick={this.handleSubscribe}
              loading={subscribing}>
              {i18n.translate('AdminHTML.Subscription.PriceBox.choosePlan')}
            </Button>
            <p className="subscribe-money-back">
              {i18n.translate('AdminHTML.Common.Subscription.moneyBackGuarantee')}
            </p>
          </>
        ) : (
          ''
        )}
        {warningMessage && <div className="warning">{warningMessage}</div>}
        {showDiscountCodeInput && this.renderDiscountNotice()}
        <TermsConditionsModal
          active={activeTermsConditionsModal}
          handleChangeModal={this.handleChangeModal}
          content={termsConditionsContent}
          subscribe={this.handleSubscribeFromModal}
        />
      </div>
    );
  }

  renderDiscountNotice = () => {
    const { i18n, percent, suggestDiscountCode } = this.props;

    return suggestDiscountCode === 'Boost-BFCM21' && percent > 0 ? (
      <div style={{ paddingTop: '1rem', textAlign: 'center' }}>
        <p>
          {i18n.translate('AdminHTML.Subscription.PriceBox.discount_Notice_1', {
            t1: (
              <span style={{ fontWeight: 600 }}>
                <TextStyle variation="negative">
                  {i18n.translate(`AdminHTML.Subscription.PriceBox.discount_Notice_1_t1`)}
                </TextStyle>
              </span>
            ),
            t2: (
              <TextStyle variation="strong">
                {i18n.translate(`AdminHTML.Subscription.PriceBox.discount_Notice_1_t2`)}
              </TextStyle>
            )
          })}
        </p>
        <p>
          {i18n.translate('AdminHTML.Subscription.PriceBox.discount_Notice_2', {
            t1: (
              <TextStyle variation="strong">
                {i18n.translate(`AdminHTML.Subscription.PriceBox.discount_Notice_2_t1`)}
              </TextStyle>
            ),
            t2: (
              <TextStyle variation="strong">
                {i18n.translate(`AdminHTML.Subscription.PriceBox.discount_Notice_2_t2`)}
              </TextStyle>
            ),
            t3: (
              <TextStyle variation="strong">
                {i18n.translate(`AdminHTML.Subscription.PriceBox.discount_Notice_2_t3`)}
              </TextStyle>
            )
          })}
        </p>
      </div>
    ) : (
      ''
    );
  };

  // Toggle change state
  toggleChangeState = (id: string) => {
    const value = this.state[id];
    this.setState({ [id]: !value });
  };

  // Change discount code
  handleChangeCode = code => {
    this.props.handleChangeCode(code);
  };

  // Verify discount code
  verify = () => {
    const code = this.props.code;
    this.props.verify(code);
  };

  // Handle subscribe
  handleSubscribe = () => {
    const {
      host,
      planId,
      code = '',
      subscribe,
      showDiscountCodeInput,
      showVerifyCodeNotice,
      percent
    } = this.props;

    const codeFormat = code.replace(/\s/g, '');
    // Plan id is not valid
    if (!planId) {
      return;
    }

    // Show Terms & Conditions modal
    if (showDiscountCodeInput && showVerifyCodeNotice && codeFormat && percent) {
      this.setState({ activeTermsConditionsModal: true });
      return;
    }

    // Subscribe a plan
    subscribe(planId, codeFormat, host);
  };

  // Handle subscribe from Terms & Conditions modal
  handleSubscribeFromModal = () => {
    const { host, planId, code = '', subscribe, i18n } = this.props;

    // Plan id is not valid
    if (!planId) {
      return;
    }
    // Remove spaces
    const codeFormat = code.replace(/\s/g, '');

    // Subscribe a plan
    subscribe(planId, codeFormat, host, i18n);
  };

  handleChangeModal = () => {
    this.setState(({ activeTermsConditionsModal }) => ({
      activeTermsConditionsModal: !activeTermsConditionsModal
    }));
  };

  calculateExtraFee = products => {
    const { extraFeatures } = this.props;
    let extraFeeTotal = 0;
    let extraFeaturesFee = [];
    _.map(extraFeatures, feature => {
      switch (feature) {
        case EXTRA_FEATURES.VARIANTS_DISPLAY: {
          const fee =
            products > 5000 && products <= 10000
              ? VARIANTS_DISPLAY_EXTRA_FEE_5K_PRODUCTS
              : products > 10000
              ? VARIANTS_DISPLAY_EXTRA_FEE_10K_PRODUCTS
              : 0;
          if (fee) {
            extraFeeTotal += fee;
            extraFeaturesFee.push({ type: feature, fee: fee });
          }
          break;
        }
        default:
      }
    });
    return { extraFeeTotal, extraFeaturesFee };
  };
}

export default withI18n()(PriceBox);
